/**
 * Умные подсказки и фразы виджета практики на «Дом» · 10 JUL 2026.
 *
 * Спека (все фразы и правила): COORDINATION/specs/home_practice_widget_copy.md
 * [ack Genie 10 JUL 2026 · решения 1в/2а/3а]. Мокап: mockup_home_homework_hybrid.html.
 *
 * Принципы:
 *  - чистые функции: дата и состояние приходят аргументами (тестируемость);
 *  - ротация фраз ДЕТЕРМИНИРОВАНА от даты — внутри дня фраза стабильна,
 *    никакого Math.random в рендере (StrictMode-safe);
 *  - анти-повтор: индекс фразы идёт прогрессией по дням — соседние дни не совпадают;
 *  - слот один: максимум одна подсказка в день; после 2 дней с подсказками
 *    подряд — тихий день. E1 «разбор готов» вне лимитов (решение 3а);
 *  - тексты гендер-нейтральны, ноль эмодзи, без вины (голосовые правила §0).
 *
 * Хранилище: qastudio.v1.nudges (localStorage, НЕ синкается — шум, не прогресс).
 */

import type { HomeworkV2 } from '@/data/homework-v2';
import type { RadarAxis } from '@/lib/practiceStats';
import { StorageKeys, readStorage, writeStorage } from '@/lib/localStorage';

// ── типы ──────────────────────────────────────────────────────────────

export type NudgeType = 'razbor' | 'newPack' | 'weakAxis' | 'streak';

export interface Nudge {
  /** стабильный id события: razbor:{hwId} · newpack:{sprintId} · weakAxis:{axis} · streak */
  id: string;
  type: NudgeType;
  /** ярлык-майка слота: «Разбор готов» */
  kindLabel: string;
  text: string;
  cta: string;
}

export interface NudgesState {
  /** type → dateKey последнего показа (кулдауны типов) */
  lastShownByType: Partial<Record<NudgeType, string>>;
  /** type → dateKey дисмисса (E3/E4: тип молчит 7 дней) */
  typeMutedAt: Partial<Record<NudgeType, string>>;
  /** id события → навсегда скрыто (крестик / клик по CTA) */
  dismissed: Record<string, true>;
  /** dateKey дней, когда подсказка показывалась (для тихих дней) */
  shownDates: string[];
  /** id подсказки, показанной последней (лимит «одна в день») */
  lastShownId?: string;
}

export const EMPTY_NUDGES_STATE: NudgesState = {
  lastShownByType: {},
  typeMutedAt: {},
  dismissed: {},
  shownDates: [],
};

// ── хранилище ─────────────────────────────────────────────────────────

export function readNudgesState(): NudgesState {
  const raw = readStorage<Partial<NudgesState>>(StorageKeys.nudges, {});
  return {
    lastShownByType: raw.lastShownByType ?? {},
    typeMutedAt: raw.typeMutedAt ?? {},
    dismissed: raw.dismissed ?? {},
    shownDates: Array.isArray(raw.shownDates) ? raw.shownDates : [],
    lastShownId: raw.lastShownId,
  };
}

export function writeNudgesState(state: NudgesState): void {
  writeStorage(StorageKeys.nudges, state);
}

// ── ротация фраз ──────────────────────────────────────────────────────

/** djb2 — детерминированный не-крипто хеш для ротации фраз. */
export function hashStr(s: string): number {
  let h = 5381;
  for (let i = 0; i < s.length; i += 1) h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
  return h;
}

/**
 * Индекс фразы слота на день (спека §4.1-4.2): арифметическая прогрессия
 * idx = (base + dayNumber · step) % n, где step ∈ [1, n-1] выводится из slotId.
 * Свойства: детерминирован внутри дня (StrictMode-safe), соседние дни
 * НИКОГДА не совпадают (step % n ≠ 0), все фразы банка проходят по кругу.
 */
export function pickPhraseIdx(slotId: string, dateKey: string, n: number): number {
  if (n <= 1) return 0;
  const dayNumber = Math.floor(parseKey(dateKey) / 86_400_000);
  const base = hashStr(slotId) % n;
  const step = (hashStr(`${slotId}#step`) % (n - 1)) + 1;
  return (((base + dayNumber * step) % n) + n) % n;
}

/** Безопасное взятие из банка фраз (noUncheckedIndexedAccess). */
function at<T>(arr: ReadonlyArray<T>, idx: number): T {
  return (arr[idx] ?? arr[0]) as T;
}

// ── утилиты дат (dateKey = YYYY-MM-DD, как dayKey в practiceStats) ────

function parseKey(k: string): number {
  const p = k.split('-').map(Number);
  return new Date(p[0] ?? 1970, (p[1] ?? 1) - 1, p[2] ?? 1).getTime();
}

/** Полных дней между dateKey'ами (b − a). Некорректные ключи → Infinity. */
export function daysBetween(a: string | undefined, b: string): number {
  if (!a) return Infinity;
  const ms = parseKey(b) - parseKey(a);
  return Number.isFinite(ms) ? Math.round(ms / 86_400_000) : Infinity;
}

// ── банки фраз · слот подсказки (спека §1) ────────────────────────────

const RAZBOR_PHRASES: ReadonlyArray<(p: { title: string; est: number }) => string> = [
  (p) => `«${p.title}» — сдано. Открылся эталон и типовые ошибки: ${p.est} минут, пока тема тёплая.`,
  (p) => `По «${p.title}» готов разбор. Сравни своё решение с эталоном — самая быстрая прокачка из возможных.`,
  (p) => `Разбор «${p.title}» ждёт. Пара типовых ошибок там — почти наверняка про всех нас.`,
  (p) => `Сдача принята, разбор открыт. Загляни в эталон «${p.title}» — увидишь, что можно было проще.`,
];

const NEWPACK_PHRASES: ReadonlyArray<
  (p: { n: number; theme: string; code: string; lesson: string | null; est: number }) => string
> = [
  (p) =>
    p.lesson
      ? `Со спринтом открылись ${p.n} домашек по теме «${p.theme}». Первая продолжает то, что читаешь в ${p.lesson}.`
      : `Со спринтом открылись ${p.n} домашек по теме «${p.theme}». Начни с первой — она задаёт тон остальным.`,
  (p) => `В спринте появилась практика: ${p.n} задач. Начни с ${p.code} — она ложится прямо на урок дня.`,
  (p) => `${p.n} новых задач ждут рук. Первая — ${p.est} минут, и в портфолио станет на артефакт больше.`,
];

const WEAKAXIS_PHRASES: ReadonlyArray<
  (p: { axis: string; done: number; total: number; est: number }) => string
> = [
  (p) => `Ось «${p.axis}» пока самая тонкая — ${p.done} из ${p.total}. Одна задача сегодня заметно её подтянет.`,
  (p) => `Радар подсказывает: «${p.axis}» отстаёт от остальных. Есть задача на ${p.est} минут — ровно по ней.`,
  (p) => `В «${p.axis}» давно не было практики. Навык любит короткие регулярные подходы.`,
];

/** E4 · решение Genie 1в: без привязки к времени суток, спокойный утренний тон. */
const STREAK_PHRASES: ReadonlyArray<(p: { n: number; est: number }) => string> = [
  (p) => `Серия идёт ${p.n}-й день — сегодняшняя клетка ждёт. Хватит ${p.est} минут практики.`,
  (p) => `${p.n} дней подряд — ровный ритм. Короткая задача уже на верстаке, серия продолжится сама.`,
  () => 'В сегодняшней клетке пока пусто. Один заход — и серия станет длиннее на день.',
];

// ── деривация подсказки (спека §1 + §4) ───────────────────────────────

export interface NudgeInput {
  todayKey: string;
  yesterdayKey: string;
  state: NudgesState;
  /** E1: домашка, сданная в последние 48ч (разбор к ней открыт) */
  razborHw: HomeworkV2 | null;
  /** E2: активный спринт с нетронутой пачкой домашек */
  newPack: { sprintId: string; count: number; theme: string; first: HomeworkV2; lessonId: string | null } | null;
  /** E3: слабейшая ось (<40%, ≥3 задач) + доступная задача по ней */
  weakAxis: { axis: RadarAxis; hw: HomeworkV2 } | null;
  /** E4: серия ≥3 дней и сегодня 0 минут */
  streak: { days: number; estMin: number } | null;
}

const COOLDOWN_DAYS: Record<NudgeType, number> = {
  razbor: 0, // событийный — без кулдауна
  newPack: 3,
  weakAxis: 5,
  streak: 3,
};

const TYPE_MUTE_DAYS = 7;

function typeAllowed(type: NudgeType, state: NudgesState, todayKey: string): boolean {
  if (daysBetween(state.typeMutedAt[type], todayKey) < TYPE_MUTE_DAYS) return false;
  const cd = COOLDOWN_DAYS[type];
  if (cd > 0 && daysBetween(state.lastShownByType[type], todayKey) < cd) return false;
  return true;
}

function buildCandidates(input: NudgeInput): Nudge[] {
  const { todayKey } = input;
  const out: Nudge[] = [];

  if (input.razborHw) {
    const hw = input.razborHw;
    const id = `razbor:${hw.id}`;
    const idx = pickPhraseIdx(id, todayKey, RAZBOR_PHRASES.length);
    out.push({
      id,
      type: 'razbor',
      kindLabel: 'Разбор готов',
      text: at(RAZBOR_PHRASES, idx)({ title: hw.title, est: 5 }),
      cta: 'К разбору →',
    });
  }

  if (input.newPack) {
    const p = input.newPack;
    const id = `newpack:${p.sprintId}`;
    const idx = pickPhraseIdx(id, todayKey, NEWPACK_PHRASES.length);
    out.push({
      id,
      type: 'newPack',
      kindLabel: 'Новая практика',
      text: at(NEWPACK_PHRASES, idx)({
        n: p.count,
        theme: p.theme,
        code: p.first.code,
        lesson: p.lessonId,
        est: p.first.estimateMin,
      }),
      cta: `Открыть ${p.first.code} →`,
    });
  }

  if (input.weakAxis) {
    const { axis, hw } = input.weakAxis;
    const id = `weakAxis:${axis.axis}`;
    const idx = pickPhraseIdx(id, todayKey, WEAKAXIS_PHRASES.length);
    out.push({
      id,
      type: 'weakAxis',
      kindLabel: 'Слабая ось',
      text: at(WEAKAXIS_PHRASES, idx)({
        axis: axis.label,
        done: axis.done,
        total: axis.total,
        est: hw.estimateMin,
      }),
      cta: 'К задаче оси →',
    });
  }

  if (input.streak) {
    const id = 'streak';
    const idx = pickPhraseIdx(id, todayKey, STREAK_PHRASES.length);
    out.push({
      id,
      type: 'streak',
      kindLabel: `Стрик · ${input.streak.days} ${pluralDays(input.streak.days)}`,
      text: at(STREAK_PHRASES, idx)({ n: input.streak.days, est: input.streak.estMin }),
      cta: 'Продолжить серию →',
    });
  }

  return out;
}

/**
 * Выбор подсказки дня. Порядок приоритета E1→E4; фильтры: dismissed,
 * кулдауны, мьюты типов, лимит «одна в день», тихий день после 2 дней
 * с подсказками подряд. E1 вне лимитов (решение 3а).
 */
export function deriveNudge(input: NudgeInput): Nudge | null {
  const { state, todayKey, yesterdayKey } = input;
  const candidates = buildCandidates(input).filter((c) => !state.dismissed[c.id]);
  if (candidates.length === 0) return null;

  // E1 — событийный: мимо тихих дней и лимита «одна в день».
  const razbor = candidates.find((c) => c.type === 'razbor');
  if (razbor) return razbor;

  // Лимит «одна в день»: если сегодня уже показывали — держим ту же подсказку.
  if (state.shownDates.includes(todayKey)) {
    const same = candidates.find((c) => c.id === state.lastShownId);
    return same ?? null;
  }

  // Тихий день: подсказки были вчера И позавчера подряд.
  const dayBefore = daysBetween(yesterdayKey, todayKey) === 1
    ? shiftKey(yesterdayKey, -1)
    : null;
  const quiet =
    state.shownDates.includes(yesterdayKey) &&
    dayBefore !== null &&
    state.shownDates.includes(dayBefore);
  if (quiet) return null;

  return candidates.find((c) => typeAllowed(c.type, state, todayKey)) ?? null;
}

function shiftKey(key: string, days: number): string {
  const d = new Date(parseKey(key) + days * 86_400_000);
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

/** Фиксация показа (идемпотентно — безопасно для StrictMode double-run). */
export function recordNudgeShown(state: NudgesState, nudge: Nudge, todayKey: string): NudgesState {
  if (state.lastShownId === nudge.id && state.shownDates.includes(todayKey)) return state;
  const shownDates = state.shownDates.includes(todayKey)
    ? state.shownDates
    : [...state.shownDates, todayKey].slice(-30);
  return {
    ...state,
    shownDates,
    lastShownId: nudge.id,
    lastShownByType: { ...state.lastShownByType, [nudge.type]: todayKey },
  };
}

/** Крестик или клик по CTA: id — навсегда; для E3/E4 тип молчит 7 дней. */
export function dismissNudge(state: NudgesState, nudge: Nudge, todayKey: string): NudgesState {
  const next: NudgesState = {
    ...state,
    dismissed: { ...state.dismissed, [nudge.id]: true },
  };
  if (nudge.type === 'weakAxis' || nudge.type === 'streak') {
    next.typeMutedAt = { ...next.typeMutedAt, [nudge.type]: todayKey };
  }
  return next;
}

// ── причина рекомендации (спека §2) ───────────────────────────────────

export type ReasonKind = 'resume' | 'themed' | 'fresh' | 'tail' | 'clear';

const RESUME_REASONS: ReadonlyArray<(p: { k: number; m: number }) => string> = [
  (p) => `Чеклист уже ${p.k} из ${p.m}. Начатое легче закрыть, пока помнишь ход.`,
  (p) => `Работа стоит на ${p.k}/${p.m}. Вернуться дешевле, чем начинать заново.`,
  (p) => `Тут уже вложено время: ${p.k} из ${p.m} пунктов. Дожми — и артефакт уйдёт в портфолио.`,
];

/** R1 при пустом чеклисте (k=0): без счёта, чтобы не звучать абсурдно. */
const RESUME_ZERO_REASONS: ReadonlyArray<() => string> = [
  () => 'Уже открыта — самый трудный первый шаг сделан. Вернись и поставь первую галочку.',
  () => 'Чеклист пока пуст, но старт есть. Один пункт сегодня — и появится ход.',
  () => 'Вернись к начатому: первый пункт чеклиста обычно самый быстрый.',
];

const THEMED_REASONS: ReadonlyArray<(p: { ticket: string }) => string> = [
  (p) => `Та же тема, что в ${p.ticket}, только руками. Урок + практика в один день запоминаются лучше всего.`,
  (p) => `Продолжает урок дня: сначала ${p.ticket} глазами, потом это — руками.`,
  (p) => `Пара к ${p.ticket}: теория утром, артефакт вечером.`,
];

const FRESH_REASONS: ReadonlyArray<(p: { est: number }) => string> = [
  (p) => `Следующая по плану спринта. ${p.est} минут — и плюс один артефакт.`,
  () => 'Очередной шаг практики. Начни — дальше втянешься.',
  () => 'Свежая задача, ещё не тронута. Твой ход.',
];

const TAIL_REASONS: ReadonlyArray<(p: { oldSprint: string; est: number }) => string> = [
  (p) => `В активном спринте всё закрыто — остался хвост из ${p.oldSprint}. Закроешь — дорожка чистая.`,
  (p) => `Хвост ${p.oldSprint} не горит, но сам не исчезнет. ${p.est} минут — и его нет.`,
];

/** R5 · решение Genie 2а: Артель упоминаем. */
const CLEAR_PHRASES: ReadonlyArray<() => string> = [
  () => 'Все домашки открытых спринтов сданы. Можно вернуться к разборам — или заглянуть в Артель.',
  () => 'Чисто. Следующая пачка придёт вместе с новым спринтом.',
];

export interface ReasonParams {
  k?: number;
  m?: number;
  ticket?: string;
  est?: number;
  oldSprint?: string;
}

export function reasonText(
  kind: ReasonKind,
  params: ReasonParams,
  todayKey: string,
): string {
  const slot = `reason:${kind}`;
  switch (kind) {
    case 'resume': {
      if ((params.k ?? 0) === 0) {
        const zIdx = pickPhraseIdx(`${slot}:zero`, todayKey, RESUME_ZERO_REASONS.length);
        return at(RESUME_ZERO_REASONS, zIdx)();
      }
      const idx = pickPhraseIdx(slot, todayKey, RESUME_REASONS.length);
      return at(RESUME_REASONS, idx)({ k: params.k ?? 0, m: params.m ?? 0 });
    }
    case 'themed': {
      const idx = pickPhraseIdx(slot, todayKey, THEMED_REASONS.length);
      return at(THEMED_REASONS, idx)({ ticket: params.ticket ?? '' });
    }
    case 'fresh': {
      const idx = pickPhraseIdx(slot, todayKey, FRESH_REASONS.length);
      return at(FRESH_REASONS, idx)({ est: params.est ?? 0 });
    }
    case 'tail': {
      const idx = pickPhraseIdx(slot, todayKey, TAIL_REASONS.length);
      return at(TAIL_REASONS, idx)({ oldSprint: params.oldSprint ?? '', est: params.est ?? 0 });
    }
    case 'clear': {
      const idx = pickPhraseIdx(slot, todayKey, CLEAR_PHRASES.length);
      return at(CLEAR_PHRASES, idx)();
    }
  }
}

// ── подпись «Ритма недели» (спека §3) ─────────────────────────────────

export interface RhythmInput {
  todayKey: string;
  goalMin: number;
  activeMinToday: number;
  streak: number;
  /** дней с последней активности (0 = сегодня уже была) */
  gapDays: number;
  /** активных дней на этой неделе (пн-вс) */
  weekActiveDays: number;
  /** день недели ISO: 0 = понедельник */
  dow: number;
}

export function rhythmCaption(p: RhythmInput): string {
  const { todayKey } = p;
  // S1 · цель дня закрыта
  if (p.activeMinToday >= p.goalMin) {
    const bank = [
      `цель дня закрыта — ${p.activeMinToday} мин в копилке`,
      `на сегодня собрано — ${p.activeMinToday} мин`,
    ];
    return at(bank, pickPhraseIdx('rhythm:S1', todayKey, bank.length));
  }
  // S2 · серия живёт
  if (p.streak >= 2) {
    const left = Math.max(1, p.goalMin - p.activeMinToday);
    const bank = [
      `серия живёт ${p.streak}-й день — до цели ещё ${left} мин`,
      `${p.streak} ${pluralDays(p.streak)} подряд — сегодняшняя клетка ждёт`,
    ];
    return at(bank, pickPhraseIdx('rhythm:S2', todayKey, bank.length));
  }
  // S3 · после перерыва
  if (p.gapDays >= 3) {
    const bank = [
      'перерыв случается у всех — клетка сегодня перезапустит ритм',
      'ритм легко вернуть: одна клетка сегодня',
    ];
    return at(bank, pickPhraseIdx('rhythm:S3', todayKey, bank.length));
  }
  // S4 · неделя только начинается
  if (p.weekActiveDays === 0 && p.dow <= 1) {
    return 'неделя только начинается — первая клетка сегодня';
  }
  // S5 · дефолт
  return `каждые ${p.goalMin} мин практики закрашивают клетку`;
}

// ── мелочи ────────────────────────────────────────────────────────────

export function pluralDays(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'день';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'дня';
  return 'дней';
}
